import { useCallback, useMemo } from 'react';

import { generatePath } from 'react-router-dom';

import { useBusinessEntity } from '@zenbusiness/application-commons-business-entity';
import { useExperimentFlag } from '@zenbusiness/application-commons-experiments';
import ButtonBase from '@zenbusiness/zui/ButtonBase';

import { getTimelineItems } from '#client/components/OrderTracker/utils';
import { EIN_CREATION_JOURNEY_PATH, LLC_FORMATION_JOURNEY_PATH } from '#client/constants';
import { ENABLE_ORDER_TRACKER_RA_IR } from '#client/constants/experimentsFlags';

import { useEnterpriseFulfillments } from './useEnterpriseFulfillments';

// Fulfillment statuses where the customer can act to move the service forward.
const ACTIONABLE_STATUSES = ['action_req', 'incomplete'];

// Maps a service type to the path its actionable row links to. Types not listed
// stay status-only.
const SERVICE_PATHS = {
  ein_creation: EIN_CREATION_JOURNEY_PATH,
  llc_formation: LLC_FORMATION_JOURNEY_PATH,
};

/**
 * Logic for the enterprise OrderTracker: reads orders-api fulfillments, builds the
 * timeline items (live status), and attaches the per-row link props for actionable
 * (link) rows. Display labels are resolved by the component.
 *
 * @returns {{
 *   loading: boolean,
 *   items: Array<object>
 * }}
 */
export function useEnterpriseOrderTracker() {
  const { items: fulfillments, loading } = useEnterpriseFulfillments();
  const { businessEntityUuid } = useBusinessEntity();
  const enableRaIr = useExperimentFlag(ENABLE_ORDER_TRACKER_RA_IR, false);

  const getServicePath = useCallback(
    (type, jurisdictionCode) => {
      const basePath = SERVICE_PATHS[type];

      if (!basePath || !businessEntityUuid) {
        return '';
      }

      // Include the jurisdiction segment when available so journeys-ui can resolve
      // the correct config without depending on domesticJurisdiction (enterprise
      // entities may lack a proposed_business record on orders-api).
      if (jurisdictionCode) {
        const pathWithJurisdiction = basePath.replace('/:businessEntityUuid', '/:jurisdiction/:businessEntityUuid');
        return generatePath(pathWithJurisdiction, {
          jurisdiction: jurisdictionCode.toLowerCase(),
          businessEntityUuid,
        });
      }

      // A relative path resolves against the current origin natively as an anchor href.
      return generatePath(basePath, { businessEntityUuid });
    },
    [businessEntityUuid],
  );

  const getLinkProps = useCallback(
    (item) => {
      const href = ACTIONABLE_STATUSES.includes(item.status) ? getServicePath(item.type, item.jurisdictionCode) : '';

      // reserveChevronSpace keeps the status pill in a consistent right-side
      // column across actionable (chevron) and status-only rows (GROWTH-4667).
      if (href) {
        return {
          component: ButtonBase,
          clickstreamProps: { elementName: 'enterprise_order_tracker_item' },
          href,
          isClickable: true,
          reserveChevronSpace: true,
          showChevron: true,
        };
      }

      return {
        isClickable: false,
        reserveChevronSpace: true,
        showChevron: false,
      };
    },
    [getServicePath],
  );

  const timelineItems = useMemo(
    () => getTimelineItems(fulfillments, { keepLiveStatus: true, enableRaIr }),
    [fulfillments, enableRaIr],
  );

  const items = useMemo(
    () =>
      timelineItems.map((item) => ({
        ...item,
        linkProps: getLinkProps(item),
      })),
    [timelineItems, getLinkProps],
  );

  return {
    items,
    loading,
  };
}
